import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { OrgNoteEditor } from "@/components/org-note-editor";
import { useTRPC } from "@/trpc";

interface ExpenseOrgNoteProps {
  expenseId: string;
  organizationBusinessUnitId: string;
  orgNote: string | null;
  canEdit: boolean;
  popover?: boolean;
}

export function ExpenseOrgNote({
  expenseId,
  organizationBusinessUnitId,
  orgNote,
  canEdit,
  popover = false,
}: ExpenseOrgNoteProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const updateMutation = useMutation(
    trpc.expense.updateOrgNote.mutationOptions({
      onSuccess: async () => {
        toast.success("Org note saved");
        setOpen(false);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.expense.getPendingExpensesForOrg.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.expense.getApprovedExpensesForOrg.queryKey(),
          }),
        ]);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save org note");
      },
    }),
  );

  return (
    <OrgNoteEditor
      orgNote={orgNote}
      canEdit={canEdit}
      popover={popover}
      inputId={`expense-org-note-${expenseId}`}
      open={open}
      onOpenChange={setOpen}
      isSaving={updateMutation.isPending}
      onSave={(nextOrgNote) =>
        updateMutation.mutate({
          organizationBusinessUnitId,
          expenseId,
          orgNote: nextOrgNote,
        })
      }
    />
  );
}
